// @ts-strict-ignore
import { useCallback, useState, useRef } from 'react';
import type { FC } from 'react';
import {
    ClickAwayListener,
    Grow,
    MenuItem,
    MenuList,
    Paper,
    Popper,
    Button,
    Box,
} from '@mui/material';
import { FormattedMessage, useIntl } from 'react-intl';
import { useFragment, graphql } from 'react-relay';
import { createDownloadFeedbackPQRUrl } from '../../services/url';
import { DownloadFileHelper } from '../DownloadFileHelper';
import ToggleArrowsUpAndDown from '../ToggleArrowsUpAndDown';
import type { ResultFilter } from '../../schema.graphql';
import type { DownloadPQiFeedbackButton_survey$key } from './__generated__/DownloadPQiFeedbackButton_survey.graphql';

type DownloadPQiFeedbackButtonProps = {
    survey: DownloadPQiFeedbackButton_survey$key;
    filters?: ReadonlyArray<ResultFilter>;
    filterMode?: 'LogicalAnd' | 'LogicalOr';
};

const DownloadPQiFeedbackButton: FC<DownloadPQiFeedbackButtonProps> = (
    props,
) => {
    const survey = useFragment(surveyFragment, props.survey);
    const { locale } = useIntl();
    const [open, setOpen] = useState(false);
    const anchorRef = useRef(null);

    const [url, setUrl] = useState<string>('');
    const link = createDownloadFeedbackPQRUrl(
        survey.projectRawId,
        survey.surveyNumber,
        props.filters,
        props.filterMode,
        locale,
    );

    const handleClickDownloadWithComments = useCallback(
        (event) => {
            event.preventDefault();
            setOpen(false);
            setUrl(link);
        },
        [link],
    );
    const handleClickDownloadWithoutComments = useCallback(
        (event) => {
            event.preventDefault();
            setOpen(false);
            setUrl(link + '&skipComments=true');
        },
        [link],
    );

    const handleToggle = () => {
        setOpen(!open);
    };

    const handleClose = (event: MouseEvent | TouchEvent) => {
        if (anchorRef.current?.contains(event.target)) {
            return;
        }

        setOpen(false);
    };

    return (
        <>
            <Button
                ref={anchorRef}
                color="primary"
                disabled={!survey.resultsUnlocked}
                onClick={handleToggle}
                className={'nope' /*classes.reportDownloadButton*/}
                variant={'outlined'}
                sx={(theme) => ({
                    backgroundColor: theme.palette.background.paper,
                    width: '80%',
                })}
                endIcon={
                    <ToggleArrowsUpAndDown
                        toggle={open}
                        size={12}
                        className={'nope' /*classes.downloadFeedbackArrow*/}
                    />
                }
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1, // Adds space between the text and the icon (1 is theme spacing, ~8px)
                    }}
                >
                    <FormattedMessage
                        defaultMessage="Download"
                        description="Download"
                    />
                </Box>
            </Button>
            <Popper
                open={open}
                transition
                disablePortal
                anchorEl={anchorRef.current}
                role={undefined}
            >
                {({ TransitionProps, placement }) => (
                    <Grow
                        {...TransitionProps}
                        style={{
                            transformOrigin:
                                placement === 'bottom'
                                    ? 'center top'
                                    : 'center bottom',
                        }}
                    >
                        <Paper>
                            <ClickAwayListener onClickAway={handleClose}>
                                <MenuList id="split-button-menu">
                                    <MenuItem
                                        onClick={
                                            handleClickDownloadWithComments
                                        }
                                    >
                                        <FormattedMessage defaultMessage="Download" />
                                    </MenuItem>
                                    <MenuItem
                                        onClick={
                                            handleClickDownloadWithoutComments
                                        }
                                    >
                                        <FormattedMessage defaultMessage="Download without comments" />
                                    </MenuItem>
                                </MenuList>
                            </ClickAwayListener>
                        </Paper>
                    </Grow>
                )}
            </Popper>
            <DownloadFileHelper
                noDialog
                url={url}
                open={url !== ''}
                onClose={() => {
                    setUrl('');
                }}
            />
        </>
    );
};

const surveyFragment = graphql`
    fragment DownloadPQiFeedbackButton_survey on ProjectSurvey {
        projectRawId
        surveyNumber
        resultsUnlocked
    }
`;
export default DownloadPQiFeedbackButton;
